import { Injectable } from "@angular/core";
import { AgsSideNavItem } from "@ags/common-ui";
import { Subscription } from "rxjs";
import { SharedDataService } from "../services/sharedDataService";
import { AppComponent as BetApp } from "../bet/app.component";
import { IsServiceAdmin, IsServiceUser } from "../utility/userprofile-helper";
import { ModuleService } from "./module.service";
import { environment } from "src/environments/environment";

@Injectable({
  providedIn: "root",
})
export class MenuService {
  landingNavItems: AgsSideNavItem[] = [];
  lionrockNavItems: AgsSideNavItem[] = [];
  plannedNavItems: AgsSideNavItem[] = [];
  adminNavItems: AgsSideNavItem[] = [];
  prepNavItems: AgsSideNavItem[] = [];
  selfNavItems: AgsSideNavItem[] = [];
  ffpNavItems: AgsSideNavItem[] = [];
  pfNavItems: AgsSideNavItem[] = [];
  capacityBlueprintsNavItems: AgsSideNavItem[] = [];
  subscription: Subscription;

  private planningSection: AgsSideNavItem = {
    name: "Planning",
    children: [
      {
        name: "Service Blueprints",
        url: "/plans/all",
      },
      {
        name: "Execution Plans",
        url: "/plans/regional/all",
      },
    ],
  };

  private requestsSection: AgsSideNavItem = {
    name: "Requests",
    children: [
      {
        name: "My Requests",
        url: "/planned/my-requests",
      },
      {
        name: "All Requests",
        url: "/planned/requests",
      },
    ],
  };

  constructor(private moduleService: ModuleService, private sharedDataService: SharedDataService) {
    this.lionrockNavItems = [
      {
        name: "Region Access and Quota",
        children: [
          {
            name: "New Request",
            url: "/create-request",
          },
          {
            name: "My Requests",
            url: "/request-status",
          },
          {
            name: "All Requests",
            url: "/requests",
          },
        ],
      },
    ];

    this.plannedNavItems = [
      this.planningSection,
      this.requestsSection
    ];

    this.selfNavItems = [
      {
        name: "Access & Quota Information",
        url: "/self-service/region-information",
      },
      {
        name: "VM Sizes",
        url: "/self-service/vm-sizes",
      },
      {
        name: "Common Arm Resource Type Requirements",
        url: "/self-service/arm-resource-type",
      },
      {
        name: "Attest for S360 KPI",
        url: "self-service/odqattest",
      }
    ];

    this.ffpNavItems = [
      {
        name: "FFP",
        children: [
          {
            name: "Service Blueprints",
            url: "/ffp/all",
          },
          {
            name: "Buildout",
            url: "/ffp/buildout",
          },
        ],
      },
    ];

    this.pfNavItems = [
      {
        name: "PF Capacity",
        children: [
          {
            name: "PF Blueprints",
            url: "/pf/all",
          },
          {
            name: "Ad Hoc Requests",
            url: "/pf/ad-hoc-requests/all",
          },
        ],
      },
    ];

    this.capacityBlueprintsNavItems = [
      {
        name: "Capacity",
        children: [
          {
            name: "Blueprints",
            url: "/capacity/blueprints",
          },     
          {
            name: "Demands",
            url: "/capacity/demands",
          },
        ],
      },
    ];


    const approverAdmin = {
      name: "Approvers",
      url: "/approver-admin",
    };

    this.subscription = this.sharedDataService.getUserProfile().subscribe((res) => {
      const userProfile = res;

      // Region Access and Quota menu
      if (userProfile?.IsApprover) {
        this.lionrockNavItems.push({
          name: "Approve Requests",
          url: "/approver-review",
        });

        this.lionrockNavItems.push({
          name: "All Requests - Legacy View",
          url: "/admin/operator-review",
        });
      }

      if (userProfile?.IsAdmin) {
        this.adminNavItems.push(
          {
            name: "Management",
            children: [
              approverAdmin,
              { name: "Regions", url: "/admin-view/region" },
              { name: "AZ Config", url: "/admin-view/azconfig" },
              { name: "Edge Zones", url: "/admin-view/edgezone" },
              { name: "User Roles", url: "/admin-view/userrole" },
              { name: "Team Config", url: "/admin-view/teams/config" },
              { name: "Blueprint Config", url: "/admin-view/blueprints/config" },
              { name: "Service Handling", url: "/admin-view/servicehandling" },
              { name: "Subscription Bindings", url: "/admin-view/bindings/subscription" },
              { name: "Property Bindings", url: "/admin-view/bindings/property" },
              { name: "Manatree Config", url: "/admin-view/manatree" },
              { name: "Third Party Customer Access", url: "/admin-view/thirdparty-subscription-access" },
            ],
          },
          {
            name: "Auto Approval",
            children: [
              { name: "Basic Rules", url: "/admin-view/autoapprovalrule" },
              { name: "Hobo Subscription Rules", url: "admin-view/autoapprovalrule-hobo" },
              { name: "Regional Quota Limit", url: "/admin-view/regionalQuota" },
              { name: "Subscription Limit", url: "/admin-view/subscriptionlimit" },
              { name: "Auto Approval Threshold", url: "/admin-view/execution-plan-auto-approval-threshold" },
            ],
          }
        );

        if (environment.enableCcoTestPage) {
          this.adminNavItems.push({
            name: "Testing",
            children: [{ name: "CCO Test", url: "/admin-view/cco-test" }],
          });
        }

        // Add Lionrock Admin section to Capacity Blueprints menu for admin only
        this.capacityBlueprintsNavItems[0]?.children?.push({
          name: "Lionrock Admin",
          children: [
            {
              name: "Assign via PSL",
              url: "/capacity/blueprints/assign/PSL",
            },
            {
              name: "Assign via Project",
              url: "/capacity/blueprints/assign/project",
            },
            {
              name: "Signoff",
              url: "/capacity/signoff",
            },
          ],
        });
      }

      if (userProfile?.IsOperator) {
        this.adminNavItems.push(
          {
            name: "Views",
            children: [
              { name: "Capacity Order", url: "/admin-view/capacity-order-view" },
              { name: "Emails", url: "/admin-view/emails-view" },
              { name: "Raw Tables", url: "/admin-view/table-view" },
            ],
          },
          {
            name: "Incidents",
            children: [
              { name: "Active Sub Requests", url: "/incidents-view/active-subrequests" },
              { name: "Active Parent Requests", url: "/incidents-view/active-requests" },
              { name: "Incomplete Tickets", url: "/incidents-view/incomplete-tickets" },
            ],
          }
        );
      }

      // ApproverAdmin users can only view the Approvers management tab
      // Admins already have access to this tab through the Management section above
      if (userProfile?.IsApproverAdmin && !userProfile?.IsAdmin) {
        this.adminNavItems.push({
          name: "Management",
          children: [approverAdmin],
        });
      }

      // Planned Quota menu
      const isServiceAdmin = IsServiceAdmin(userProfile);
      const isServiceUser = isServiceAdmin || IsServiceUser(userProfile);
      if (isServiceUser || isServiceAdmin) {
        const children = [
          {
            name: "Assign Blueprint to Regions",
            url: "/plans/regional/assign",
          },
        ];

        if (isServiceAdmin) {
          this.selfNavItems.push({
            name: "My Service Team Users",
            url: "/self-service/mine/users",
          });
          this.selfNavItems.push({
            name: "My Service Team Apps",
            url: "/self-service/mine/apps",
          });
          this.selfNavItems.push({
            name: "My Trusted Services",
            url: "/self-service/mine/trusted-services",
          });
          this.selfNavItems.push({
            name: "Revoke access and quota",
            url: "/self-service/revoke-access-quota",
          });
        }

        if (isServiceUser) {
          children.push(
            {
              name: "My Execution Plans",
              url: "/plans/regional/mine",
            },
            // {
            //   name: "Sign-off Execution Plans",
            //   url: "/plans/regional/signoff",
            // },
            {
              name: "Bindings",
              url: "/plans/bindings",
            }
          );
        }

        this.planningSection.children.push({
          name: "Service Owner",
          children: children,
        });
      }

      if (userProfile?.IsAdmin || userProfile?.IsOperator) {
        this.planningSection.children.push({
          name: "Buildout Owner",
          children: [
            {
              name: "Assign Blueprints to Regions",
              url: "/plans/regions",
            },
            {
              name: "Fulfillment Status",
              url: "/plans/region",
            },
            {
              name: "Risks",
              url: "/plans/plans-risks",
            },
          ],
        });
      }

      if (userProfile?.IsApprover) {
        this.planningSection.children.push({
          name: "Approver",
          children: [
            {
              name: "Approve Execution Plans",
              url: "/plans/regional/approvable",
            },
            {
              name: "Quota Allocation Summary",
              url: "/plans/approval-info",
            },
          ],
        });
      }

      if (userProfile?.IsAdmin || !environment.production) {
        this.requestsSection.children.unshift({
          name: "Create Request",
          url: "/planned/create-request",
        });
      }

      // PF menu
      const pfPlansSection = this.pfNavItems.find((i) => i.name == "PF Capacity");
      const pfApproverChildren = [
        {
          name: "Approve PF Blueprints",
          url: "/pf/approvable",
        },
        {
          name: "Approve Ad Hoc Requests",
          url: "/pf/ad-hoc-requests/approvable",
        },
      ];
      pfPlansSection.children.push({
        name: "Approver",
        children: pfApproverChildren,
      });
      pfPlansSection.children.push({
        name: "Dashboards",
        children: [
          {
            name: "PF FFP Growth Formulas",
            url: "/pf/dashboards/pf-ffp-growth-formulas",
          },
        ],
      });

      // FFP menu
      const ffpPlansSection = this.ffpNavItems.find((i) => i.name == "FFP");
      if (isServiceUser) {
        const children = [
          /*{
            name: "Assign FFP Plan to Projects",
            url: "/ffp/plans/regional/assign",
          },*/
          {
            name: "My FFP Instances",
            url: "/ffp/instances/mine",
          },
        ];

        ffpPlansSection.children.push({
          name: "Service Owner",
          children: children,
        });
      }

      if (userProfile?.IsAdmin || userProfile?.IsFfpAdmin) {
        ffpPlansSection.children.push({
          name: "Lionrock Admin",
          children: [
            {
              name: "Assign FFP Plans To Project",
              url: "/ffp/plans/assign/admin",
            }
          ],
        });
      }

      this.prepNavItems = [
        {
          name: "Billing Meter",
          children: BetApp.prepNavItems[0].children.filter((child: any) => {
            // all the people can access if the menu not set the roles
            if (!child.Roles) {
              return true;
            }

            // check whether user profile contains the role required
            return userProfile?.UserRoles.some((i) => child.Roles.includes(i.Role));
          }),
        },
      ];
    });
  }

  getModuleNavItems(): AgsSideNavItem[] {
    switch (this.moduleService.module) {
      case "Landing":
        return this.landingNavItems;
      case "Billing Meter":
        return this.prepNavItems;
      case "Lionrock":
        return this.lionrockNavItems;
      case "Planned":
        return this.plannedNavItems;
      case "Administrator":
        return this.adminNavItems;
      case "SelfService":
        return this.selfNavItems;
      case "FFP":
        return this.ffpNavItems;
      case "PF":
        return this.pfNavItems;
      case "CapacityBlueprint":
        return this.capacityBlueprintsNavItems;
    }
  }
}
